'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import Cookies from 'js-cookie'
import UserCommentButton from '@/components/apps/UserCommentButton'
import 'flag-icons/css/flag-icons.min.css'
import { TODOS, VISIBLES } from '@/lib/idiomas'

// El menú muestra los visibles; para reconocer el idioma vale cualquiera (lib/idiomas.js).
const LOCALES = VISIBLES

const LOCALE_META = {
  es: { countryCode: 'AR', label: 'Español' },
  en: { countryCode: 'US', label: 'English' },
  pt: { countryCode: 'PT', label: 'Português' },
  fr: { countryCode: 'FR', label: 'Français' },
  it: { countryCode: 'IT', label: 'Italiano' },
  de: { countryCode: 'DE', label: 'Deutsch' },
  ja: { countryCode: 'JP', label: '日本語' },
  tr: { countryCode: 'TR', label: 'Türkçe' },
}

function getFlagClassName(cc = '') {
  const code = String(cc).toLowerCase()
  if (!/^[a-z]{2}$/.test(code)) return 'fi'
  return `fi fi-${code}`
}

export default function AppTopRightControls({ appKey = null, positionStyle = {} }) {
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()
  const router = useRouter()
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  const currentLocale = useMemo(() => {
    const firstSegment = pathname.split('/')[1] || ''
    if (TODOS.includes(firstSegment)) return firstSegment

    const active = String(i18n.resolvedLanguage || i18n.language || 'es')
      .toLowerCase()
      .split('-')[0]
    return TODOS.includes(active) ? active : 'es'
  }, [pathname, i18n.language, i18n.resolvedLanguage])

  const changeLocale = (newLocale) => {
    if (!LOCALES.includes(newLocale) || newLocale === currentLocale) return

    Cookies.set('ln', newLocale, {
      expires: 30,
      sameSite: 'Lax',
      secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
      path: '/',
    })

    // No llamamos i18n.changeLanguage() acá: el TranslationsProvider solo tiene
    // recursos del locale actual y cambiar el idioma sin recursos causa fallback
    // a 'es'. La navegación carga los recursos correctos.
    const segments = pathname.split('/')
    const firstSegment = segments[1] || ''

    if (TODOS.includes(firstSegment)) {
      segments[1] = newLocale
    } else {
      segments.splice(1, 0, newLocale)
    }

    let nextPath = segments.join('/') || '/'
    const qs = searchParams?.toString?.() || ''
    if (qs) nextPath += `?${qs}`

    router.push(nextPath)
    setOpen(false)
  }

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!rootRef.current) return
      if (rootRef.current.contains(event.target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const currentMeta = LOCALE_META[currentLocale] || LOCALE_META.es
  const currentCode = String(currentLocale || 'es').toUpperCase()
  const currentFlagClass = getFlagClassName(currentMeta.countryCode)

  return (
    <div
      ref={rootRef}
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        ...positionStyle,
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'inline-flex',
          flexDirection: 'column',
          minWidth: 84,
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={currentMeta.label}
          aria-label={`Current language: ${currentMeta.label}`}
          aria-expanded={open}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              aria-hidden
              className={currentFlagClass}
              style={{ width: 16, borderRadius: 2, overflow: 'hidden' }}
            />
            <span>{currentCode}</span>
          </span>
          <span aria-hidden style={{ opacity: 0.9 }}>▾</span>
        </button>

        {open ? (
          <div
            style={{
              position: 'absolute',
              top: 38,
              right: 0,
              width: 140,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(0,0,0,0.72)',
              backdropFilter: 'blur(8px)',
              overflow: 'hidden',
              boxShadow: '0 10px 20px rgba(0,0,0,0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.08)',
              }}
            >
              <span
                aria-hidden
                className={currentFlagClass}
                style={{ width: 16, borderRadius: 2, overflow: 'hidden' }}
              />
              <span>{currentCode}</span>
              <span style={{ marginLeft: 'auto', opacity: 0.75 }}>Now</span>
            </div>

            {LOCALES.map((locale) => {
              const meta = LOCALE_META[locale]
              const code = locale.toUpperCase()
              const active = locale === currentLocale
              const flagClass = getFlagClassName(meta.countryCode)

              return (
                <button
                  key={locale}
                  type="button"
                  onClick={() => changeLocale(locale)}
                  title={meta.label}
                  aria-label={meta.label}
                  disabled={active}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: '#fff',
                    cursor: active ? 'default' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    opacity: active ? 0.9 : 1,
                  }}
                >
                  <span
                    aria-hidden
                    className={flagClass}
                    style={{ width: 16, borderRadius: 2, overflow: 'hidden' }}
                  />
                  <span>{code}</span>
                </button>
              )
            })}
          </div>
        ) : null}
      </div>

      {appKey ? <UserCommentButton appKey={appKey} floating={false} /> : null}
    </div>
  )
}
